/**
 * Pure domain logic for Half-Depth equipment mounting, face disambiguation,
 * rear/front ghost visualization, and multi-face slot availability.
 *
 * Direct alignment with NetBox Device.face ('front' | 'rear') and Device.depth ('full' | 'half').
 */

export type NormalizedMounting = 'front' | 'rear' | 'full_depth' | 'zero_u';

export interface ConflictCheckResult {
  conflict: boolean;
  conflictType?: 'face_overlap' | 'depth_conflict';
  reason?: string;
}

export interface GhostDeviceItem {
  eq: any;
  oppositeFace: 'front' | 'rear';
  u_position: number;
  u_height: number;
  isCoexisting: boolean;
  coexistingWith?: any;
}

export interface FreeBlock {
  start: number;
  size: number;
}

export interface SlotFaceStatus {
  front: 'free' | 'occupied' | 'reserved';
  rear: 'free' | 'occupied' | 'reserved';
  isFullyFree: boolean;
  isFullyOccupied: boolean;
}

/**
 * Normalizes mounting position strings into canonical enum values.
 */
export function normalizeMountingPosition(mount?: string): NormalizedMounting {
  if (!mount) return 'front';
  const m = mount.trim().toLowerCase();
  if (m === 'left' || m === 'right' || m.includes('zero')) return 'zero_u';
  if (m === 'rear' || m === 'back') return 'rear';
  if (m.includes('full') || m === 'both' || m === 'all') return 'full_depth';
  return 'front';
}

/**
 * Checks whether two equipment items in the rack conflict with each other.
 * Allows Front and Rear half-depth devices to occupy the same U simultaneously.
 */
export function checkEquipmentConflict(
  candidate: { u_position: number; u_height: number; mounting_position?: string; name?: string },
  existing: { u_position: number; u_height: number; mounting_position?: string; name?: string }
): ConflictCheckResult {
  const candMount = normalizeMountingPosition(candidate.mounting_position);
  const existMount = normalizeMountingPosition(existing.mounting_position);

  // Zero-U items do not conflict with grid equipment
  if (candMount === 'zero_u' || existMount === 'zero_u') {
    return { conflict: false };
  }

  const candStart = Math.floor(Number(candidate.u_position));
  const candH     = Math.max(1, Math.floor(Number(candidate.u_height) || 1));
  const candEnd   = candStart + candH - 1;

  const existStart = Math.floor(Number(existing.u_position));
  const existH     = Math.max(1, Math.floor(Number(existing.u_height) || 1));
  const existEnd   = existStart + existH - 1;

  // Check if U intervals overlap
  const rangesOverlap = Math.max(candStart, existStart) <= Math.min(candEnd, existEnd);
  if (!rangesOverlap) {
    return { conflict: false };
  }

  const overlapStart = Math.max(candStart, existStart);
  const overlapEnd = Math.min(candEnd, existEnd);
  const rangeStr = overlapStart === overlapEnd ? `U${overlapStart}` : `U${overlapStart}..U${overlapEnd}`;

  // Case 1: Front and Rear half-depth devices COEXIST peacefully
  if (
    (candMount === 'front' && existMount === 'rear') ||
    (candMount === 'rear' && existMount === 'front')
  ) {
    return { conflict: false };
  }

  // Case 2: Placing Full Depth over existing Half Depth device
  if (candMount === 'full_depth' && (existMount === 'rear' || existMount === 'front')) {
    const faceName = existMount === 'rear' ? 'тыльным' : 'фронтальным';
    return {
      conflict: true,
      conflictType: 'depth_conflict',
      reason: `Конфликт глубины: слот ${rangeStr} частично занят ${faceName} устройством '${existing.name || 'Оборудование'}'`,
    };
  }

  // Case 3: Placing Half Depth over existing Full Depth device
  if ((candMount === 'front' || candMount === 'rear') && existMount === 'full_depth') {
    return {
      conflict: true,
      conflictType: 'depth_conflict',
      reason: `Конфликт глубины: слот ${rangeStr} занят полноглубинным устройством '${existing.name || 'Оборудование'}'`,
    };
  }

  // Case 4: Both are Full Depth
  if (candMount === 'full_depth' && existMount === 'full_depth') {
    return {
      conflict: true,
      conflictType: 'depth_conflict',
      reason: `Конфликт глубины: слот ${rangeStr} уже занят полноглубинным устройством '${existing.name || 'Оборудование'}'`,
    };
  }

  // Case 5: Both are on the same face (both front or both rear)
  const faceName = candMount === 'rear' ? 'тыльным' : 'фронтальным';
  return {
    conflict: true,
    conflictType: 'face_overlap',
    reason: `Конфликт: слот ${rangeStr} уже занят ${faceName} устройством '${existing.name || 'Оборудование'}'`,
  };
}

/**
 * Computes ghost equipment items to display on the opposite projection view.
 * When viewing 'front', returns rear half-depth devices.
 * When viewing 'rear', returns front half-depth devices.
 */
export function getGhostDevices(
  equipmentList: any[],
  projectionMode: 'front' | 'rear' | 'split' | string
): GhostDeviceItem[] {
  if (projectionMode !== 'front' && projectionMode !== 'rear') {
    return [];
  }

  const targetFace = projectionMode === 'front' ? 'rear' : 'front';
  const currentFace = projectionMode === 'front' ? 'front' : 'rear';

  // Filter out Zero-U items
  const nonZero = equipmentList.filter(eq => !eq.is_zero_u);

  // Group items by face
  const oppositeItems = nonZero.filter(eq => normalizeMountingPosition(eq.mounting_position) === targetFace);
  const currentItems = nonZero.filter(eq => {
    const m = normalizeMountingPosition(eq.mounting_position);
    return m === currentFace || m === 'full_depth';
  });

  return oppositeItems.map(oppositeEq => {
    const oppStart = Math.floor(Number(oppositeEq.u_position));
    const oppH = Math.max(1, Math.floor(Number(oppositeEq.u_height) || 1));
    const oppEnd = oppStart + oppH - 1;

    // Check if there is coexisting equipment in the current face
    const coexistingWith = currentItems.find(currEq => {
      const currStart = Math.floor(Number(currEq.u_position));
      const currH = Math.max(1, Math.floor(Number(currEq.u_height) || 1));
      const currEnd = currStart + currH - 1;
      return Math.max(oppStart, currStart) <= Math.min(oppEnd, currEnd);
    });

    return {
      eq: oppositeEq,
      oppositeFace: targetFace,
      u_position: oppStart,
      u_height: oppH,
      isCoexisting: !!coexistingWith,
      coexistingWith,
    };
  });
}

/**
 * Calculates free U blocks with face awareness (Front, Rear, Full Depth).
 * A U is only completely full if both front and rear (or full depth) are occupied.
 */
export function calculateFreeUWithFaces(
  totalU: number,
  equipmentList: any[],
  reservations: any[] = []
): {
  freeFullBlocks: FreeBlock[];
  freeFrontBlocks: FreeBlock[];
  freeRearBlocks: FreeBlock[];
  slotStatusMap: Record<number, SlotFaceStatus>;
} {
  const slotStatusMap: Record<number, SlotFaceStatus> = {};

  for (let u = 1; u <= totalU; u++) {
    slotStatusMap[u] = {
      front: 'free',
      rear: 'free',
      isFullyFree: true,
      isFullyOccupied: false,
    };
  }

  // 1. Mark equipment
  for (const eq of equipmentList) {
    if (eq.is_zero_u) continue;
    const start = Math.floor(Number(eq.u_position));
    const h = Math.max(1, Math.floor(Number(eq.u_height) || 1));
    const end = Math.min(totalU, start + h - 1);
    const mount = normalizeMountingPosition(eq.mounting_position);

    for (let u = start; u <= end; u++) {
      if (!slotStatusMap[u]) continue;
      if (mount === 'full_depth') {
        slotStatusMap[u].front = 'occupied';
        slotStatusMap[u].rear = 'occupied';
      } else if (mount === 'front') {
        slotStatusMap[u].front = 'occupied';
      } else if (mount === 'rear') {
        slotStatusMap[u].rear = 'occupied';
      }
    }
  }

  // 2. Mark reservations
  for (const res of reservations) {
    const start = Math.floor(Number(res.u_start));
    const h = Math.max(1, Math.floor(Number(res.u_height) || 1));
    const end = Math.min(totalU, start + h - 1);
    const face = (res.face || 'both').toLowerCase();

    for (let u = start; u <= end; u++) {
      if (!slotStatusMap[u]) continue;
      if (face === 'both' || face === 'full_depth') {
        if (slotStatusMap[u].front === 'free') slotStatusMap[u].front = 'reserved';
        if (slotStatusMap[u].rear === 'free') slotStatusMap[u].rear = 'reserved';
      } else if (face === 'front') {
        if (slotStatusMap[u].front === 'free') slotStatusMap[u].front = 'reserved';
      } else if (face === 'rear') {
        if (slotStatusMap[u].rear === 'free') slotStatusMap[u].rear = 'reserved';
      }
    }
  }

  // Update summary flags
  for (let u = 1; u <= totalU; u++) {
    const s = slotStatusMap[u];
    s.isFullyFree = s.front === 'free' && s.rear === 'free';
    s.isFullyOccupied = s.front !== 'free' && s.rear !== 'free';
  }

  // Helper to construct contiguous blocks
  const buildBlocks = (predicate: (s: SlotFaceStatus) => boolean): FreeBlock[] => {
    const blocks: FreeBlock[] = [];
    let curStart: number | null = null;
    for (let u = 1; u <= totalU; u++) {
      if (predicate(slotStatusMap[u])) {
        if (curStart === null) curStart = u;
      } else if (curStart !== null) {
        blocks.push({ start: curStart, size: u - curStart });
        curStart = null;
      }
    }
    if (curStart !== null) {
      blocks.push({ start: curStart, size: totalU - curStart + 1 });
    }
    return blocks;
  };

  const freeFullBlocks = buildBlocks(s => s.front === 'free' && s.rear === 'free');
  const freeFrontBlocks = buildBlocks(s => s.front === 'free');
  const freeRearBlocks = buildBlocks(s => s.rear === 'free');

  return {
    freeFullBlocks,
    freeFrontBlocks,
    freeRearBlocks,
    slotStatusMap,
  };
}
